import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, clearToken, getToken, setToken } from './api'

type User = { id:string; display_name?:string; phone_normalized?:string; roles?:string[] }
type AuthState = {
  user: User | null
  loading: boolean
  login: (returnPath?:string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({children}:{children:ReactNode}) {
  const [user,setUser] = useState<User|null>(null)
  const [loading,setLoading] = useState(true)

  async function refresh() {
    if (!getToken()) { setUser(null); setLoading(false); return }
    try {
      const result = await api<{user:User}>('/api/auth/me')
      setUser(result.user)
    } catch { setUser(null) }
    finally { setLoading(false) }
  }

  useEffect(() => { void refresh() }, [])

  async function login(returnPath = window.location.pathname) {
    const start = await api<{deep_link:string;poll_token:string}>('/api/auth/max/start', {
      method:'POST', body:JSON.stringify({purpose:'login', return_path:returnPath})
    })
    const popup = window.open(start.deep_link, '_blank', 'noopener,noreferrer')
    const startedAt = Date.now()
    await new Promise<void>((resolve,reject) => {
      const timer = window.setInterval(async () => {
        if (Date.now()-startedAt > 10*60*1000) {
          window.clearInterval(timer); reject(new Error('Время входа истекло')); return
        }
        try {
          const status = await api<any>(`/api/auth/max/status?token=${encodeURIComponent(start.poll_token)}`)
          if (status.status === 'completed' && status.session_token) {
            window.clearInterval(timer)
            setToken(status.session_token)
            popup?.close()
            await refresh()
            resolve()
          } else if (['failed','expired','cancelled'].includes(status.status)) {
            window.clearInterval(timer); reject(new Error('Не удалось подтвердить вход'))
          }
        } catch (error) {
          console.warn(error)
        }
      }, 1800)
    })
  }

  async function logout() {
    try { if (getToken()) await api('/api/auth/logout',{method:'POST'}) } catch {}
    clearToken(); setUser(null)
  }

  const value = useMemo(() => ({user,loading,login,logout,refresh}),[user,loading])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth():AuthState {
  const value = useContext(AuthContext)
  if (!value) throw new Error('AuthProvider missing')
  return value
}
